import {
  ApiError,
  billingIntervalLabel,
  billingIntervalUnit,
  type ChargeableCard,
  formatMoney,
  lineTaxCents,
  type PosCheckoutResult,
  queryKeys,
  SERVICE_PLAN_BILLING_INTERVALS,
  type ServicePlanBillingInterval,
  type SubscriptionPlanOption,
  type TaxCategory,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { CheckoutPaymentDialog, type PendingCheckout } from './checkout-payment-dialog.js'
import { AddCardDialog } from './saved-cards-card.js'
import { EMPTY_SIM, SimField, type SimValue } from './sim-field.js'
import {
  costProfitHint,
  dollarsToCents,
  type PlanDraft,
  ProviderSuggestions,
} from './subscription-dialogs.js'

/** The few fields of a location this dialog needs. */
interface LocationOption {
  id: string
  key: string
  isActive: boolean
}

/** The few fields of a product this dialog needs to price a SIM sold with the plan. */
interface SimProduct {
  priceCents: number
  taxCategoryId: string | null
}

/** Plan-picker value that opens the create-a-plan form instead of choosing a plan. */
const NEW_PLAN = '__new__'

const today = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** The day after `from` plus one billing interval, as YYYY-MM-DD (months and
 *  years clamp to the last day of the month, like the server does). */
function dateOneIntervalOut(interval: SubscriptionPlanOption['billingInterval']): string {
  const now = new Date()
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
  if (interval === 'thirty_days') {
    d.setUTCDate(d.getUTCDate() + 30)
  } else {
    const day = d.getUTCDate()
    d.setUTCDate(1)
    d.setUTCMonth(d.getUTCMonth() + (interval === 'year' ? 12 : 1))
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
    d.setUTCDate(Math.min(day, last))
  }
  return d.toISOString().slice(0, 10)
}

/**
 * Sells a phone plan to a customer from their own page and takes the first
 * payment on one of their saved cards in the same step (t1308). It goes through
 * the same combined checkout as New sale, so the plan is an ordinary paid order
 * and Stripe billing for the renewals is set up from it. A declined card falls
 * into the checkout's own "record another tender or cancel" dialog.
 */
export function SellPlanDialog({
  customerId,
  taxExempt,
  open,
  onClose,
  onRecordInstead,
  onSold,
}: {
  customerId: string
  taxExempt: boolean
  open: boolean
  onClose: () => void
  /** Opens the plain "record a plan set up elsewhere" dialog instead. */
  onRecordInstead: (draft: PlanDraft) => void
  onSold: () => void
}) {
  const toast = useToast()
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const canCreatePlan = can('catalog:write')
  const [creatingPlan, setCreatingPlan] = useState(false)
  const [productId, setProductId] = useState('')
  const [phoneLine, setPhoneLine] = useState('')
  // t1375: the plan is set up in full at the sale — a SIM is required.
  const [label, setLabel] = useState('')
  const [sim, setSim] = useState<SimValue>(EMPTY_SIM)
  const [provider, setProvider] = useState('')
  const [device, setDevice] = useState('')
  const [activatedAt, setActivatedAt] = useState(today())
  const [cost, setCost] = useState('')
  const [cardId, setCardId] = useState('')
  const [addingCard, setAddingCard] = useState(false)
  // t1380: add the plan without charging it now; the card is billed on this date.
  const [deferFirst, setDeferFirst] = useState(false)
  const [firstChargeDate, setFirstChargeDate] = useState('')
  const [pending, setPending] = useState<PendingCheckout | null>(null)
  // The cards on file when "Add a new card" was opened, so the one saved
  // meanwhile can be picked for staff.
  const cardsBeforeAdd = useRef<Set<string> | null>(null)

  const { data: options } = useQuery({
    queryKey: queryKeys.subscriptions.list({ planOptions: true }),
    queryFn: () => api.get<SubscriptionPlanOption[]>('/subscriptions/plan-options'),
    enabled: open,
  })
  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<LocationOption[]>('/locations'),
    enabled: open,
  })
  const { data: taxCategories } = useQuery({
    queryKey: queryKeys.taxCategories(),
    queryFn: () => api.get<TaxCategory[]>('/tax-categories'),
    enabled: open,
  })
  // Only the customer's own cards: a Stripe subscription can only renew on a
  // card that belongs to its own Stripe customer.
  const { data: cards } = useQuery({
    queryKey: queryKeys.customers.paymentMethods(customerId),
    queryFn: () => api.get<ChargeableCard[]>(`/customers/${customerId}/payment-methods`),
    enabled: open,
  })

  // A SIM picked from stock is sold on the same order, so it is priced and taxed too.
  const simUnit = sim.unit
  const { data: simProduct } = useQuery({
    queryKey: queryKeys.products.detail(simUnit?.productId ?? ''),
    queryFn: () => api.get<SimProduct>(`/products/${simUnit?.productId}`),
    enabled: open && simUnit !== null,
  })

  useEffect(() => {
    if (open) {
      setProductId('')
      setPhoneLine('')
      setLabel('')
      setSim(EMPTY_SIM)
      setProvider('')
      setDevice('')
      setActivatedAt(today())
      setCost('')
      setCardId('')
      setDeferFirst(false)
      setFirstChargeDate('')
    }
  }, [open])

  // Default to the newest card on file, or the one just added.
  useEffect(() => {
    if (!cards?.length) return
    const before = cardsBeforeAdd.current
    if (before) {
      const added = cards.find((c) => !before.has(c.id))
      if (added) {
        setCardId(added.id)
        cardsBeforeAdd.current = null
      }
      return
    }
    setCardId((current) =>
      current && cards.some((c) => c.id === current) ? current : (cards[0]?.id ?? ''),
    )
  }, [cards])

  const plan = options?.find((o) => o.productId === productId)
  // Picking another plan restarts the suggested date from its own interval.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the plan choice resets the date
  useEffect(() => {
    if (plan) setFirstChargeDate(dateOneIntervalOut(plan.billingInterval))
  }, [productId, plan?.billingInterval])
  const location = (locations ?? []).filter((l) => l.isActive)
  const locationId = (location.find((l) => l.key === 'storefront') ?? location[0])?.id ?? ''

  const taxRateById = useMemo(
    () => new Map((taxCategories ?? []).map((c) => [c.id, c.rateBps])),
    [taxCategories],
  )
  const planSubtotalCents = plan?.priceCents ?? 0
  const planTaxCents = plan
    ? lineTaxCents(plan.priceCents, 1, taxRateById.get(plan.taxCategoryId ?? ''), taxExempt)
    : 0
  const simPriceCents =
    simUnit && simProduct ? (simUnit.attributePriceCents ?? simProduct.priceCents) : 0
  const simTaxCents =
    simUnit && simProduct
      ? lineTaxCents(simPriceCents, 1, taxRateById.get(simProduct.taxCategoryId ?? ''), taxExempt)
      : 0
  const subtotalCents = planSubtotalCents + simPriceCents
  const taxCents = planTaxCents + simTaxCents
  const totalCents = subtotalCents + taxCents
  // Deferred: only a SIM taken from stock is still charged today.
  const chargedNowCents = deferFirst ? simPriceCents + simTaxCents : totalCents

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.paymentMethods(customerId) }),
    ])
  }

  const finish = async () => {
    await refresh()
    setPending(null)
    toast.success('Plan sold', 'Stripe billing for the renewals is being set up now.')
    onSold()
    onClose()
  }

  const addDeferred = useMutation({
    mutationFn: () =>
      api.post('/subscriptions/deferred', {
        customerId,
        productId,
        paymentMethodId: cardId,
        firstChargeDate,
        label: label.trim() || null,
        phoneLine: phoneLine.trim() || null,
        simIccid: sim.iccid.trim(),
        simUnitId: simUnit?.id ?? null,
        provider: provider.trim() || null,
        device: device.trim() || null,
        activatedAt: activatedAt || null,
        costCents: dollarsToCents(cost),
      }),
    onSuccess: async () => {
      await refresh()
      setPending(null)
      toast.success('Plan added', `The first charge is on ${firstChargeDate}.`)
      onSold()
      onClose()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const sell = useMutation({
    mutationFn: () =>
      api.post<PosCheckoutResult>('/checkout', {
        credits: [],
        customerId,
        locationId,
        items: [
          ...(deferFirst
            ? []
            : [
                {
                  productId,
                  qty: 1,
                  serializedUnitId: null,
                  unitPriceCents: planSubtotalCents,
                  sourceLocationId: null,
                  phoneLine: phoneLine.trim() || null,
                  identityFlag: null,
                  planSetup: {
                    label: label.trim() || null,
                    simIccid: sim.iccid.trim(),
                    simUnitId: simUnit?.id ?? null,
                    provider: provider.trim() || null,
                    device: device.trim() || null,
                    activatedAt: activatedAt || null,
                    costCents: dollarsToCents(cost),
                  },
                },
              ]),
          ...(simUnit
            ? [
                {
                  productId: simUnit.productId,
                  qty: 1,
                  serializedUnitId: simUnit.id,
                  unitPriceCents: simPriceCents,
                  sourceLocationId:
                    simUnit.locationId && simUnit.locationId !== locationId
                      ? simUnit.locationId
                      : null,
                  phoneLine: null,
                  identityFlag: null,
                },
              ]
            : []),
        ],
        rentals: [],
        note: null,
        expectedTotalCents: chargedNowCents,
        payment: { mode: 'card', method: 'saved_card', paymentMethodId: cardId },
      }),
    onSuccess: () => (deferFirst ? addDeferred.mutate() : finish()),
    onError: (error: Error) => {
      // A declined card still opened the order — staff can record another
      // tender for it, or cancel the sale.
      const checkoutId =
        error instanceof ApiError && error.code === 'CARD_DECLINED'
          ? ((error.details as { checkoutId?: string } | undefined)?.checkoutId ?? null)
          : null
      if (checkoutId) {
        setPending({ checkoutId, clientSecret: null, publishableKey: null, method: 'saved_card' })
      }
      toast.error(error.message)
    },
  })

  const free = plan !== undefined && plan.priceCents <= 0
  const simReady = simUnit === null || simProduct !== undefined
  // A free stock SIM has no sale to run, so the deferred path needs a priced one
  // or a typed number.
  const freeStockSim = deferFirst && simUnit !== null && chargedNowCents <= 0
  const dateOk = !deferFirst || (firstChargeDate > today() && firstChargeDate.length === 10)
  const canSell =
    Boolean(plan) &&
    !free &&
    Boolean(cardId) &&
    Boolean(locationId) &&
    Boolean(sim.iccid.trim()) &&
    simReady &&
    dateOk &&
    !freeStockSim
  const busy = sell.isPending || addDeferred.isPending
  const onSubmit = () => {
    // Deferred with no stock SIM: nothing to charge, just add the plan.
    if (deferFirst && !simUnit) addDeferred.mutate()
    else sell.mutate()
  }

  return (
    <>
      <Dialog open={open && !pending} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add plan</DialogTitle>
            <DialogDescription>
              Charges the first period to a card on file and sets up automatic Stripe billing for
              the renewals — or adds the plan now and starts billing on a later date.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Field label="Plan">
              {(props) => (
                <select
                  {...props}
                  className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
                  value={productId}
                  onChange={(e) => {
                    if (e.target.value === NEW_PLAN) setCreatingPlan(true)
                    else setProductId(e.target.value)
                  }}
                >
                  <option value="">Choose a plan…</option>
                  {options?.map((o) => (
                    <option key={o.productId} value={o.productId} disabled={o.priceCents <= 0}>
                      {o.name} — {formatMoney(o.priceCents)} /{' '}
                      {billingIntervalUnit(o.billingInterval)}
                      {o.priceCents <= 0 ? ' (free — record it instead)' : ''}
                    </option>
                  ))}
                  {canCreatePlan ? <option value={NEW_PLAN}>+ Create new plan…</option> : null}
                </select>
              )}
            </Field>
            <Field label="Name" hint="Who this line is for, e.g. Mrs Smith.">
              {(props) => (
                <Input
                  {...props}
                  maxLength={80}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              )}
            </Field>
            <Field label="Phone line" hint="Optional — the number this plan is for.">
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  inputMode="tel"
                  maxLength={30}
                  value={phoneLine}
                  onChange={(e) => setPhoneLine(e.target.value)}
                  placeholder="The number this plan is for"
                />
              )}
            </Field>
            <SimField value={sim} onChange={setSim} label="SIM (ICCID)" />
            <Field label="Provider" hint="Who supplies the service, e.g. US Mobile.">
              {(props) => (
                <Input
                  {...props}
                  maxLength={60}
                  list="plan-providers"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                />
              )}
            </Field>
            <ProviderSuggestions />
            <Field label="Activated device">
              {(props) => (
                <Input
                  {...props}
                  maxLength={120}
                  value={device}
                  onChange={(e) => setDevice(e.target.value)}
                />
              )}
            </Field>
            <Field label="Activation date">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={activatedAt}
                  onChange={(e) => setActivatedAt(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Our cost ($)"
              hint={
                plan
                  ? `What we pay the provider each ${billingIntervalUnit(plan.billingInterval)}. ${costProfitHint(plan.monthlyCents, cost)}`
                  : 'What we pay the provider each billing period.'
              }
            >
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                />
              )}
            </Field>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Pay with</legend>
              {cards?.length ? (
                <ul className="space-y-1.5">
                  {cards.map((card) => (
                    <li key={card.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2.5 py-2 text-sm">
                        <input
                          type="radio"
                          name="sell-plan-card"
                          checked={cardId === card.id}
                          onChange={() => setCardId(card.id)}
                        />
                        <span className="capitalize">{card.brand}</span> ···· {card.last4}
                        <span className="ml-auto text-xs text-foreground-muted">
                          {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-foreground-muted">No card on file yet.</p>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  cardsBeforeAdd.current = new Set((cards ?? []).map((c) => c.id))
                  setAddingCard(true)
                }}
              >
                Add a new card
              </Button>
            </fieldset>

            <div className="space-y-2">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={deferFirst}
                  onChange={(e) => setDeferFirst(e.target.checked)}
                />
                Don't charge the plan now — first charge next cycle
              </label>
              {deferFirst ? (
                <Field
                  label="First charge date"
                  hint={
                    plan
                      ? `The card is charged ${formatMoney(plan.monthlyCents)} on this date, then every ${billingIntervalUnit(plan.billingInterval)}.`
                      : 'The card is charged on this date.'
                  }
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      min={today()}
                      value={firstChargeDate}
                      onChange={(e) => setFirstChargeDate(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
              {freeStockSim ? (
                <p className="text-xs text-danger">
                  That stock SIM has no price, so there is nothing to charge today — type its number
                  instead.
                </p>
              ) : null}
            </div>

            {plan ? (
              <dl className="space-y-1 rounded-md bg-surface-muted p-3 text-sm">
                <div className="flex justify-between">
                  <dt>Plan</dt>
                  <dd>
                    {deferFirst
                      ? `first charge ${firstChargeDate}`
                      : formatMoney(planSubtotalCents)}
                  </dd>
                </div>
                {simUnit ? (
                  <div className="flex justify-between">
                    <dt>SIM</dt>
                    <dd>{formatMoney(simPriceCents)}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between">
                  <dt>Tax</dt>
                  <dd>{formatMoney(deferFirst ? simTaxCents : taxCents)}</dd>
                </div>
                <div className="flex justify-between font-medium">
                  <dt>Charged now</dt>
                  <dd>{formatMoney(chargedNowCents)}</dd>
                </div>
                <p className="pt-1 text-xs text-foreground-muted">
                  {deferFirst
                    ? `Then ${formatMoney(plan.monthlyCents)} / ${billingIntervalUnit(plan.billingInterval)} starting ${firstChargeDate}, charged to the same card.`
                    : `Then ${formatMoney(plan.monthlyCents)} / ${billingIntervalUnit(plan.billingInterval)} from the next renewal, charged to the same card.`}
                </p>
              </dl>
            ) : null}
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="ghost"
              onClick={() =>
                onRecordInstead({
                  productId,
                  label,
                  phoneLine,
                  simIccid: sim.iccid,
                  provider,
                  device,
                  activatedAt,
                  cost,
                })
              }
            >
              Record a plan set up elsewhere instead
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!canSell} loading={busy} onClick={onSubmit}>
                {!plan
                  ? 'Charge'
                  : deferFirst
                    ? chargedNowCents > 0
                      ? `Charge ${formatMoney(chargedNowCents)} & add plan`
                      : 'Add plan'
                    : `Charge ${formatMoney(totalCents)}`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreatePlanDialog
        open={creatingPlan}
        taxCategories={taxCategories ?? []}
        onClose={() => setCreatingPlan(false)}
        onCreated={(created) => {
          queryClient.setQueryData<SubscriptionPlanOption[]>(
            queryKeys.subscriptions.list({ planOptions: true }),
            (old) => [...(old ?? []), created].sort((a, b) => a.name.localeCompare(b.name)),
          )
          void queryClient.invalidateQueries({
            queryKey: queryKeys.subscriptions.list({ planOptions: true }),
          })
          void queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
          setProductId(created.productId)
          setCreatingPlan(false)
        }}
      />
      <AddCardDialog customerId={customerId} open={addingCard} onOpenChange={setAddingCard} />
      <CheckoutPaymentDialog
        pending={pending}
        onSettled={() => void (deferFirst ? addDeferred.mutate() : finish())}
        onCancelled={() => {
          setPending(null)
          void refresh()
        }}
      />
    </>
  )
}

/**
 * Defines a plan type that is not in the system yet (t1379). It is saved as an
 * ordinary plan product, so it is offered on every future sale; its Stripe
 * price is made from the amount and interval when it is first sold.
 */
function CreatePlanDialog({
  open,
  taxCategories,
  onClose,
  onCreated,
}: {
  open: boolean
  taxCategories: TaxCategory[]
  onClose: () => void
  onCreated: (plan: SubscriptionPlanOption) => void
}) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [interval, setInterval] = useState<ServicePlanBillingInterval>('month')
  const [taxCategoryId, setTaxCategoryId] = useState('')

  useEffect(() => {
    if (open) {
      setName('')
      setPrice('')
      setInterval('month')
      setTaxCategoryId('')
    }
  }, [open])

  const priceCents = dollarsToCents(price)
  const create = useMutation({
    mutationFn: () =>
      api.post<SubscriptionPlanOption>('/subscriptions/plan-options', {
        name: name.trim(),
        priceCents,
        billingInterval: interval,
        taxCategoryId: taxCategoryId || null,
      }),
    onSuccess: (created) => {
      toast.success('Plan created', 'It is saved and available for every future sale.')
      onCreated(created)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create new plan</DialogTitle>
          <DialogDescription>
            Saved to the catalog for future sales, and selected for this one.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Plan name">
            {(props) => (
              <Input
                {...props}
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Price per period ($)">
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            )}
          </Field>
          <Field label="Renews">
            {(props) => (
              <select
                {...props}
                className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
                value={interval}
                onChange={(e) => setInterval(e.target.value as ServicePlanBillingInterval)}
              >
                {SERVICE_PLAN_BILLING_INTERVALS.map((i) => (
                  <option key={i} value={i}>
                    {billingIntervalLabel(i)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Tax category">
            {(props) => (
              <select
                {...props}
                className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
                value={taxCategoryId}
                onChange={(e) => setTaxCategoryId(e.target.value)}
              >
                <option value="">Not taxed</option>
                {taxCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || !priceCents || priceCents <= 0}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
